export function timeout(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function generateParams<T extends Record<string, string | number>>(
  filter: T,
): string {
  const params = new URLSearchParams()

  Object.keys(filter).forEach((key) => {
    const value = filter[key]
    if (value !== undefined && value !== null) {
      params.append(key, String(value))
    }
  })

  return params.toString()
}
export function convertNumberToFormatted(number: string | number): string {
  if (+number >= 1_000_000_000) {
    return `${+number / 1_000_000_000} میلیارد`
  } else if (+number >= 1_000_000) {
    return `${+number / 1_000_000} میلیون`
  } else {
    return number.toLocaleString()
  }
}

export function slugify(text: string): string {
  return text
    ? text
        .trim()
        .replace(/[^\u0600-\u06FF\s]/g, '') // Keep only Persian characters and spaces
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace R multiple hyphens with a single hyphen
    : ''
}

type ClassValue =
  | string
  | number
  | boolean
  | undefined
  | null
  | ClassValue[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | { [key: string]: any }

/**
 * Function to combine class names conditionally.
 *
 * @param classes - A list of class values that can be strings, arrays, objects, etc.
 * @returns A string of combined class names.
 */
export function cn(...classes: ClassValue[]): string {
  return classes
    .flatMap((cls) => {
      if (typeof cls === 'string' || typeof cls === 'number') {
        return cls
      } else if (Array.isArray(cls)) {
        return cn(...cls)
      } else if (typeof cls === 'object' && cls !== null) {
        return (
          Object.entries(cls)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .filter(([_, value]) => Boolean(value))
            .map(([key]) => key)
        )
      }
      return []
    })
    .filter(Boolean)
    .join(' ')
}

// Usage examples:
// const className = cn(
//   "btn",
//   { "btn-primary": true, "btn-disabled": false },
//   ["extra-class", { "another-class": true }]
// );
export const toValidMobileNumber = (mobileNumber: string): string => {
  const digits: string = mobileNumber.replace(/^(\+98|0|0098)?9/, '09')
  return digits
}

export function obfuscateString(
  input: string,
  startIndex = 0,
  length?: number,
  showEndLength?: number,
) {
  if (!length) {
    length = input.length
  }
  if (!showEndLength) {
    showEndLength = 0
  }
  const prefix = input.slice(0, startIndex)
  const stars = '*'.repeat(length)
  const suffix = input.slice(input.length - showEndLength)
  return prefix + stars + suffix
}

export const isValidIranianNationalCode = (input: string): boolean => {
  const invalidCodePattern = /^(\d)\1{9}$/

  if (!/^\d{10}$/.test(input) || invalidCodePattern.test(input)) {
    return false
  }

  const checkDigit = parseInt(input.charAt(9), 10)
  const sum =
    Array.from(input.substring(0, 9))
      .map((char, index) => parseInt(char, 10) * (10 - index))
      .reduce((acc, current) => acc + current, 0) % 11

  return sum < 2 ? checkDigit === sum : checkDigit + sum === 11
}

export const scrollToId = (id: string, offset = 0) => {
  const element = document.getElementById(id)
  if (element) {
    const elementPosition = element.getBoundingClientRect().top + window.scrollY
    window.scrollTo({
      top: elementPosition - offset,
      behavior: 'smooth',
    })
  }
}

export const get2MinuetsLaterInTimestamp = () => {
  return Date.now() + 2 * 60 * 1000
}

export const convertRialToToman = (number: string): number | string => {
  const parseNumber = parseInt(number)
  if (typeof parseNumber === 'number') {
    if (parseNumber > 0) {
      return +parseInt((parseNumber / 10).toString())
    } else {
      return 0
    }
  }
  return ''
}

export const addSeparatorToPrice = (number: string | number) => {
  let parseNumber = number
  if (typeof parseNumber === 'string') {
    parseNumber = parseInt(parseNumber)
  }
  if (typeof parseNumber !== 'number') return '-'
  if (parseNumber === 0) return 0
  const modified: string[] = []
  // @ts-ignore
  const numberArray = [...number.toString()].reverse()
  numberArray.map((item, index) => {
    modified.unshift(item)
    if ((index + 1) % 3 === 0 && index + 1 !== numberArray.length) {
      modified.unshift(',')
    }
  })
  return modified.join('')
}

export const getTomanCurrency = (number: number): string => {
  if (isNaN(number)) return ''
  return addSeparatorToPrice(convertRialToToman(number.toString())).toString()
}

export const isValidMobileNumber = (mobileNumber: string): boolean => {
  const regex = /^(09)\d{9}$/
  return regex.test(mobileNumber)
}

export const isValidBankCardNumber = (cardNumber?: string): boolean => {
  if (!cardNumber) return true
  if (!/^\d{16}$/.test(cardNumber)) {
    return false
  }

  let sum = 0
  let shouldDouble = false

  for (let i = cardNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cardNumber.charAt(i), 10)

    if (shouldDouble) {
      digit *= 2
      if (digit > 9) {
        digit -= 9
      }
    }

    sum += digit
    shouldDouble = !shouldDouble
  }

  return sum % 10 === 0
}

export const isValidBourseCode = (bourseCode?: string): boolean => {
  if (!bourseCode) return true
  const bourseCodePattern = /^[آ-ی]{3}[-\s]?\d{5}$/
  return bourseCodePattern.test(bourseCode)
}
